import { Postman } from "../Postman";
import { GuestUser } from "../user";
import { EventFiringClient } from "../EventFiringClient";
import { OAuth2Token } from "./OAuth2Token";
import { OAuth2Grant } from "./OAuth2Grant";
// import { Promise } from "es6-promise";
export class OAuth2ClientHandlers {
}
/**
 * oauth2 client implementation for auvious' services.
 *
 * At the moment a login method with the password grant is supported.
 *
 * More info: https://alexbilbie.com/guide-to-oauth-2-grants
 */
export class OAuth2Client extends EventFiringClient {
    constructor(clientId, clientSecret, serverUrl = "/", saveTokenOnLocalStorage = true, eventHandlers = null, useStandardOAuth2 = false, timers) {
        super("OAuth2Client", eventHandlers);
        this.timers = timers;
        this.TOKEN_STORAGE_KEY = "auvious.oauth2.client.token";
        this.LOGIN_ROUTE = "/security/authenticate/login";
        this.GUEST_ROUTE = "/security/guest";
        this._clientId = clientId;
        this._clientSecret = clientSecret;
        this._saveTokenOnLocalStorage =
            saveTokenOnLocalStorage && typeof localStorage !== "undefined";
        this._useStandardOAuth2 = useStandardOAuth2;
        this._postman = new Postman({
            baseURL: serverUrl,
            // basic authorization for client id & secret
            // auth: {
            //     username: this._clientId,
            //     password: this._clientSecret
            // },
            withCredentials: true,
            tokenRefreshRoute: OAuth2Client.TOKEN_REFRESH_ROUTE,
            onRefreshTokenExpired: this.refreshTokenExpired.bind(this),
            onRefreshToken: this.refreshTokenRequested.bind(this),
        });
        this.loadToken();
        this.log.info("client authenticated: " + this.isAuthenticated);
    }
    get _defaultHandlers() {
        return {
            onAuthorized: (ev) => {
                this.log.info("onAuthorized", ev);
            },
            onAuthorizedFailed: (ev) => {
                this.log.error("onAuthorizedFailed", ev);
            },
            onLogout: () => {
                this.log.info("onLogout");
            },
            onRefreshTokenExpired: () => {
                this.log.info("onRefreshTokenExpired");
            },
        };
    }
    /**
     * Gets the oauth2 client id.
     */
    get clientId() {
        return this._clientId;
    }
    /**
     * Gets the oauth2 client secret.
     */
    get clientSecret() {
        return this._clientSecret;
    }
    /**
     * Gets the current token of the authenticated user. Can be undefined.
     */
    get token() {
        return this._token;
    }
    /**
     * Checks if the token instance exists.
     *
     * When an authenticated client gets an authentication error (due to expiration)
     * it should issue a refresh_token grant.
     *
     * However, non authenticated client should issue a password grant to login.
     */
    get isAuthenticated() {
        return !!this.token;
    }
    get postman() {
        return this._postman;
    }
    /**
     * Exposes the internal auth event handlers.
     *
     * @param handlers : OAuth2ClientHandlers
     */
    attachOAuth2Handler(handlers) {
        for (const key in handlers) {
            if (handlers[key]) {
                this._eventHandlers[key] = handlers[key];
            }
        }
    }
    /**
     * Handles the refresh of the expired token.
     */
    refreshTokenRequested() {
        return this.refreshToken();
    }
    /**
     * Fires the corresponding event of the expired token.
     */
    refreshTokenExpired() {
        if (this._eventHandlers.onRefreshTokenExpired) {
            this._eventHandlers.onRefreshTokenExpired();
        }
    }
    /**
     * Login method for guest.
     *
     * @param clientId the subdomain of the request.
     * @param email: email of guest user.
     * @param organization: the organization to sign in / fetched from url params.
     * @param firstName: first name of guest user.
     * @param lastName: last name of guest user (optional).
     */
    loginGuest(clientId, email, organization, firstName, lastName) {
        if (!clientId || clientId.length === 0) {
            throw new Error("Invalid clientId. Pass a non empty string instance for the clientId" +
                " into the first argument");
        }
        if (!email || email.length === 0) {
            throw new Error("Invalid email. Pass a non empty string instance for the email" +
                " into the second argument");
        }
        if (!firstName || firstName.length === 0) {
            throw new Error("Invalid first name. Pass a non empty string instance for the first" +
                " name into the third argument");
        }
        if (!organization || organization.length === 0) {
            throw new Error("Invalid organization. Pass a non empty string instance for the" +
                " organization into the fourth argument");
        }
        return new Promise((resolve, reject) => {
            this.postman
                .post(this.GUEST_ROUTE, {
                clientId,
                email,
                organization,
                firstName,
                lastName,
            }, {
            // params here
            })
                .then((res) => {
                // HTTP 200 invalid credentials
                if (res.data.error) {
                    this._eventHandlers.onAuthorizedFailed(res.data);
                    return reject(res.data);
                }
                this.log.info("auvious guest login success");
                const guest = new GuestUser(res.data);
                resolve(guest);
            })
                .catch((err) => {
                this.log.warn("auvious login failed: ", err.data);
                this._eventHandlers.onAuthorizedFailed(err.data);
                reject(err.data);
            });
        });
    }
    /**
     * Login method w/o password grant.
     *
     * @param clientId the subdomain of the request.
     * @param username user username to login.
     * @param password user password to validate.
     * @param organization the organization to sign in.
     */
    login(clientId, username, password, organization) {
        if (!clientId || clientId.length === 0) {
            throw new Error("Invalid clientId. Pass a non empty string instance for the clientId" +
                " into the first argument");
        }
        if (!username || username.length === 0) {
            throw new Error("Invalid username. Pass a non empty string instance for the username" +
                " into the second argument");
        }
        if (!password || password.length === 0) {
            throw new Error("Invalid password. Pass a non empty string instance for the password" +
                " into the third argument");
        }
        if (!organization || organization.length === 0) {
            throw new Error("Invalid organization. Pass a non empty string instance for the" +
                " organization into the fourth argument");
        }
        const body = {
            clientId,
            username,
            password,
            organization,
        };
        const params = {};
        return new Promise((resolve, reject) => {
            this.postman
                .post(this.LOGIN_ROUTE, body, params)
                .then((res) => {
                // HTTP 200 invalid credentials
                if (res.data.error) {
                    this._eventHandlers.onAuthorizedFailed(res.data);
                    return reject(res.data);
                }
                this._token = new OAuth2Token(this, res.data);
                this.saveToken(this._token);
                this.log.info("auvious login success: ", this._token);
                this._eventHandlers.onAuthorized(this._token);
                resolve(this._token);
                this.setupPreemptiveTokenRefresh();
            })
                .catch((err) => {
                this.log.warn("auvious login failed: ", err.data);
                this._eventHandlers.onAuthorizedFailed(err.data);
                reject(err.data);
            });
        });
    }
    loginTemplate(tokenProviderFn, refreshTokenProviderFn) {
        this.tokenProviderFn = tokenProviderFn;
        this.refreshTokenProviderFn = refreshTokenProviderFn;
        return new Promise((resolve, reject) => {
            this.tokenProviderFn()
                .then((response) => {
                this._token = this._token = new OAuth2Token(this, response);
                this.saveToken(this._token);
                this.log.info("auvious login success: ", this._token);
                this._eventHandlers.onAuthorized(this._token);
                resolve(this._token);
                if (this.refreshTokenProviderFn) {
                    this.setupPreemptiveTokenRefresh();
                }
            })
                .catch((error) => {
                this.log.error("auvious login error: ", error);
                this._eventHandlers.onAuthorizedFailed(error);
                reject(error);
            });
        });
    }
    /**
     * Standard OAuth2 Login.
     *
     * @param username username
     * @param password password
     */
    standardOAuth2Login(username, password) {
        return new Promise((resolve, reject) => {
            const params = new URLSearchParams();
            params.append("client_id", OAuth2Client.CLIENT_ID);
            params.append("grant_type", "password");
            params.append("username", username);
            params.append("password", password);
            this.postman
                .post(OAuth2Client.OAUTH2_TOKEN_ROUTE, params)
                .then((res) => {
                // HTTP 200 invalid credentials
                if (res.data.error) {
                    this._eventHandlers.onAuthorizedFailed(res.data);
                    return reject(res.data);
                }
                this._token = new OAuth2Token(this, res.data);
                this.saveToken(this._token);
                this.log.info("auvious login success: ", this._token);
                this._eventHandlers.onAuthorized(this._token);
                resolve(this._token);
                this.setupPreemptiveTokenRefresh();
            })
                .catch((err) => {
                this.log.warn("auvious login failed: ", err.data);
                this._eventHandlers.onAuthorizedFailed(err.data);
                reject(err.data);
            });
        });
    }
    /**
     * Simpler (used by Epi) guest login.
     *
     * @param username: username of guest user.
     * @param firstName: first name of guest user (optional).
     * @param lastName: last name of guest user (optional).
     */
    guestLogin(username, firstName, lastName) {
        if (!username || username.length === 0) {
            throw new Error("Invalid email. Pass a non empty string instance for the email" +
                " into the second argument");
        }
        if (!firstName || firstName.length === 0) {
            throw new Error("Invalid first name. Pass a non empty string instance for the first" +
                " name into the third argument");
        }
        return new Promise((resolve, reject) => {
            this.postman
                .post(this.GUEST_ROUTE, {
                clientId: "meeting",
                username,
                organization: "auviousteam",
                firstName,
                lastName,
            }, {
            // params here
            })
                .then((res) => {
                // HTTP 200 invalid credentials
                if (res.data.error) {
                    this._eventHandlers.onAuthorizedFailed(res.data);
                    return reject(res.data);
                }
                this.log.info("auvious guest login success");
                const guest = new GuestUser(res.data);
                resolve(guest);
            })
                .catch((err) => {
                this.log.warn("auvious login failed: ", err.data);
                this._eventHandlers.onAuthorizedFailed(err.data);
                reject(err.data);
            });
        });
    }
    /**
     * Refresh method with refresh_token grant.
     *
     * @param token: oauth2 token to refresh.
     */
    refreshToken(tokenToRefresh = null) {
        if (tokenToRefresh) {
            this._token = tokenToRefresh;
        }
        if (!this.isAuthenticated) {
            throw new Error("Not authenticated. Cannot refresh the token " +
                " of a non authenticated client, login first.");
        }
        this.log.info("refreshing token with grant type " + OAuth2Grant.REFRESH_TOKEN + "...");
        if (this.refreshTokenProviderFn) {
            return this.refreshTokenUsingProviderFn();
        }
        else if (this._useStandardOAuth2) {
            return this.standardOAuth2RefreshToken();
        }
        else {
            return this.auviousOAuth2RefreshToken();
        }
    }
    /**
     * Logout the client.
     *
     * Cleans the oauth2 token from the client.
     */
    logout() {
        return new Promise((resolve, reject) => {
            if (this.isAuthenticated) {
                this.log.info("logging out...");
                this.deleteToken();
                if (this.preemptiveTokenRefreshTask) {
                    clearTimeout(this.preemptiveTokenRefreshTask);
                    this.preemptiveTokenRefreshTask = null;
                }
                this._eventHandlers.onLogout();
            }
            resolve();
        });
    }
    refreshTokenUsingProviderFn() {
        return new Promise((resolve, reject) => {
            this.refreshTokenProviderFn(this.token.refreshToken)
                .then((res) => {
                if (this._token) {
                    this._token.importJSON(res);
                }
                else {
                    this._token = new OAuth2Token(this, res);
                }
                this.saveToken(this._token);
                this.log.info("refresh token success: ", this._token);
                this._eventHandlers.onAuthorized(this._token);
                resolve(this._token);
                this.setupPreemptiveTokenRefresh();
            })
                .catch((err) => {
                this.deleteToken();
                this.log.warn("refresh token failed: ", err.data);
                this._eventHandlers.onAuthorizedFailed(err.data);
                reject(err.data);
            });
        });
    }
    standardOAuth2RefreshToken() {
        return new Promise((resolve, reject) => {
            const params = new URLSearchParams();
            params.append("client_id", OAuth2Client.CLIENT_ID);
            params.append("grant_type", "refresh_token");
            params.append("refresh_token", this.token.refreshToken);
            this.postman
                .post(OAuth2Client.OAUTH2_TOKEN_ROUTE, params)
                .then((res) => {
                if (this._token) {
                    this._token.importJSON(res.data);
                }
                else {
                    this._token = new OAuth2Token(this, res.data);
                }
                this.saveToken(this._token);
                this.log.info("refresh token success: ", this._token);
                this._eventHandlers.onAuthorized(this._token);
                resolve(this._token);
                this.setupPreemptiveTokenRefresh();
            })
                .catch((err) => {
                this.deleteToken();
                this.log.warn("refresh token failed: ", err.data);
                this._eventHandlers.onAuthorizedFailed(err.data);
                reject(err.data);
            });
        });
    }
    auviousOAuth2RefreshToken() {
        return new Promise((resolve, reject) => {
            this.postman
                .post(OAuth2Client.TOKEN_REFRESH_ROUTE, {
                refreshToken: this.token.refreshToken,
            }, {
            // params here
            })
                .then((res) => {
                if (this._token) {
                    this._token.importJSON(res.data);
                }
                else {
                    this._token = new OAuth2Token(this, res.data);
                }
                this.saveToken(this._token);
                this.log.info("refresh token success: ", this._token);
                this._eventHandlers.onAuthorized(this._token);
                resolve(this._token);
                this.setupPreemptiveTokenRefresh();
            })
                .catch((err) => {
                this.deleteToken();
                this.log.warn("refresh token failed: ", err.data);
                this._eventHandlers.onAuthorizedFailed(err.data);
                reject(err.data);
            });
        });
    }
    // IFDO: The following parts work only on browser platforms.
    //       To make the lib platform independent this client
    //       should accept a callback for each of the method below.
    //
    //       Then instead of localStorage.method we can call the
    //       consumer defined callbacks.
    /**
     * Loads the token json from local storage.
     * Returns undefined if no exists.
     */
    loadToken() {
        if (this._saveTokenOnLocalStorage) {
            this.log.info("loading session token...");
            const json = localStorage.getItem(this.TOKEN_STORAGE_KEY);
            if (!(json === null || json === undefined)) {
                this._token = new OAuth2Token(this, json);
                if (!this._token.isExpired) {
                    this.setupPreemptiveTokenRefresh();
                }
            }
        }
        return undefined;
    }
    /**
     * Saves the token json to local storage.
     * If not authenticated, it ensures the token json is deleted from local storage.
     */
    saveToken(token) {
        this.log.info("saving session token...");
        if (this.isAuthenticated) {
            if (this._saveTokenOnLocalStorage) {
                localStorage.setItem(this.TOKEN_STORAGE_KEY, token.toJSONStringified());
            }
        }
        else {
            this.log.info("session token not found.");
            this.deleteToken();
        }
    }
    /**
     * Removes the token json from local storage.
     */
    deleteToken() {
        this.log.info("deleting session token...");
        if (this._saveTokenOnLocalStorage) {
            localStorage.removeItem(this.TOKEN_STORAGE_KEY);
        }
        this._token = undefined;
    }
    setupPreemptiveTokenRefresh() {
        if (!this._token) {
            this.log.info("setup preemtive token refresh called while logged out!");
            return;
        }
        if (!this._token.expiresIn) {
            throw new Error("expiresIn not found!");
        }
        // automatically refresh token a few seconds before it expires to prevent
        // unecessary unauthorized calls
        let timeout = (this._token.expiresIn - 45) * 1000;
        const minTimeout = 30000;
        if (timeout < minTimeout) {
            this.log.warn(`timeout is too small (${timeout}ms) will default to ${minTimeout}ms`);
            timeout = minTimeout;
        }
        this.log.info(`will refresh in ${timeout}ms`);
        this.preemptiveTokenRefreshTask = this.timers.setTimeout(() => {
            if (this.isAuthenticated) {
                this.log.info("time to refresh token");
                this.refreshToken();
            }
            else {
                this.log.warn("time to refresh token, but not authenticated :(");
            }
        }, timeout);
    }
}
OAuth2Client.CLIENT_ID = "auvious";
OAuth2Client.TOKEN_REFRESH_ROUTE = "/security/authenticate/refresh";
OAuth2Client.OAUTH2_TOKEN_ROUTE = "/security/oauth/token";
